import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { usePaintStore } from "../../state/paintStore";
import type { ToolId } from "../../state/paintStore";
import { BucketTool } from "../../tools/BucketTool";
import { ImageStampTool } from "../../tools/ImageStampTool";
import { MarkerTool } from "../../tools/MarkerTool";
import { ShapeTool } from "../../tools/ShapeTool";
import { TextTool } from "../../tools/TextTool";
import type { Tool, ToolContext, ToolPoint } from "../../tools/Tool";
import { useHistory } from "../../hooks/useHistory";
import { TextOverlay } from "../TextOverlay/TextOverlay";
import type { TextPromptState } from "../TextOverlay/TextOverlay";
import { loadImage } from "../../utils/loadImage";
import "./Canvas.css";

export interface CanvasHandle {
  clear: () => void;
  getCanvas: () => HTMLCanvasElement | null;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  stampImage: (path: string, scale: number) => Promise<void>;
}

const ERASER_COLOR = { id: "eraser", name: "Eraser", hex: "#ffffff" } as const;

interface CanvasProps {
  backgroundColor?: string;
}

interface BuildToolArgs {
  promptText: (p: ToolPoint, commit: (text: string) => void) => void;
  shapeKind: import("../../constants/shapes").ShapeKind;
  onCommit: () => void;
}

function buildTool(id: ToolId, args: BuildToolArgs): Tool {
  switch (id) {
    case "marker":
      return new MarkerTool();
    case "eraser":
      return new MarkerTool();
    case "bucket":
      return new BucketTool();
    case "text":
      return new TextTool(args.promptText);
    case "image":
      return new ImageStampTool();
    case "shape":
      return new ShapeTool(args.shapeKind, args.onCommit);
  }
}

interface FloatingImage {
  img: HTMLImageElement;
  bitmapX: number;
  bitmapY: number;
  bitmapW: number;
  bitmapH: number;
}

export const Canvas = forwardRef<CanvasHandle, CanvasProps>(function Canvas(
  { backgroundColor = "#ffffff" },
  ref,
) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const activeTool = usePaintStore((s) => s.activeTool);
  const color = usePaintStore((s) => s.color);
  const brushSize = usePaintStore((s) => s.brushSize);
  const shapeKind = usePaintStore((s) => s.shapeKind);
  const history = useHistory();

  const [textPrompt, setTextPrompt] = useState<TextPromptState | null>(null);
  const [floatingImage, setFloatingImage] = useState<FloatingImage | null>(
    null,
  );
  const floatingImageRef = useRef<FloatingImage | null>(null);
  const placementDraggingRef = useRef(false);
  const commitRef = useRef<() => void>(() => {});

  const toolRef = useRef<Tool>(
    buildTool("marker", {
      promptText: (p, commit) => setTextPrompt({ x: p.x, y: p.y, commit }),
      shapeKind: "rectangle",
      onCommit: () => commitRef.current(),
    }),
  );

  useEffect(() => {
    floatingImageRef.current = floatingImage;
  }, [floatingImage]);

  const getToolContext = useCallback((): ToolContext => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext("2d")!;
    const effectiveColor = activeTool === "eraser" ? ERASER_COLOR : color;
    return { canvas, ctx, color: effectiveColor, brushSize };
  }, [color, brushSize, activeTool]);

  const fillBackground = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, [backgroundColor]);

  const snapshot = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    history.push(ctx.getImageData(0, 0, canvas.width, canvas.height));
  }, [history]);

  const commitFloating = useCallback(() => {
    const floating = floatingImageRef.current;
    const canvas = canvasRef.current;
    if (!floating || !canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(
      floating.img,
      floating.bitmapX,
      floating.bitmapY,
      floating.bitmapW,
      floating.bitmapH,
    );
    floatingImageRef.current = null;
    setFloatingImage(null);
    placementDraggingRef.current = false;
    snapshot();
  }, [snapshot]);

  useEffect(() => {
    commitRef.current = snapshot;
  }, [snapshot]);

  // Rebuild active tool. If a floating image is in flight when the user
  // switches tools, commit it first so the pick isn't silently lost.
  useEffect(() => {
    if (floatingImageRef.current) {
      commitFloating();
    }
    toolRef.current.cancel?.(getToolContext());
    toolRef.current = buildTool(activeTool, {
      promptText: (p, commit) => setTextPrompt({ x: p.x, y: p.y, commit }),
      shapeKind: shapeKind.id,
      onCommit: () => commitRef.current(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTool]);

  useEffect(() => {
    const tool = toolRef.current as Tool & {
      setKind?: (id: import("../../constants/shapes").ShapeKind) => void;
    };
    tool.setKind?.(shapeKind.id);
  }, [shapeKind]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const prev = document.createElement("canvas");
      prev.width = canvas.width;
      prev.height = canvas.height;
      const prevCtx = prev.getContext("2d");
      if (prevCtx && canvas.width > 0 && canvas.height > 0) {
        prevCtx.drawImage(canvas, 0, 0);
      }
      canvas.width = Math.floor(canvas.clientWidth * dpr);
      canvas.height = Math.floor(canvas.clientHeight * dpr);
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        if (prev.width > 0 && prev.height > 0) {
          ctx.drawImage(prev, 0, 0, canvas.width, canvas.height);
        }
      }
      // Recenter floating preview on resize so it stays in bounds.
      const floating = floatingImageRef.current;
      if (floating) {
        const next: FloatingImage = {
          ...floating,
          bitmapX: (canvas.width - floating.bitmapW) / 2,
          bitmapY: (canvas.height - floating.bitmapH) / 2,
        };
        floatingImageRef.current = next;
        setFloatingImage(next);
      }
    };
    resize();
    snapshot();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toPoint = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>): ToolPoint => {
      const canvas = canvasRef.current!;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY,
        pressure: e.pressure,
      };
    },
    [],
  );

  const moveFloatingTo = useCallback((bitmapCenterX: number, bitmapCenterY: number) => {
    const floating = floatingImageRef.current;
    const canvas = canvasRef.current;
    if (!floating || !canvas) return;
    const x = bitmapCenterX - floating.bitmapW / 2;
    const y = bitmapCenterY - floating.bitmapH / 2;
    const next: FloatingImage = { ...floating, bitmapX: x, bitmapY: y };
    floatingImageRef.current = next;
    setFloatingImage(next);
  }, []);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!canvasRef.current) return;
      if (textPrompt) return;
      canvasRef.current.setPointerCapture(e.pointerId);
      const point = toPoint(e);
      if (floatingImageRef.current) {
        placementDraggingRef.current = true;
        moveFloatingTo(point.x, point.y);
        return;
      }
      toolRef.current.onPointerDown(getToolContext(), point);
    },
    [getToolContext, moveFloatingTo, toPoint, textPrompt],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (e.buttons === 0) return;
      if (floatingImageRef.current && placementDraggingRef.current) {
        const point = toPoint(e);
        moveFloatingTo(point.x, point.y);
        return;
      }
      toolRef.current.onPointerMove(getToolContext(), toPoint(e));
    },
    [getToolContext, moveFloatingTo, toPoint],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (canvasRef.current?.hasPointerCapture(e.pointerId)) {
        canvasRef.current.releasePointerCapture(e.pointerId);
      }
      if (floatingImageRef.current) {
        commitFloating();
        return;
      }
      toolRef.current.onPointerUp(getToolContext(), toPoint(e));
      if (activeTool !== "text" && !toolRef.current.manualSnapshot) snapshot();
    },
    [activeTool, commitFloating, getToolContext, snapshot, toPoint],
  );

  const handleTextCommit = useCallback(
    (text: string) => {
      if (textPrompt) {
        textPrompt.commit(text);
        snapshot();
      }
      setTextPrompt(null);
    },
    [snapshot, textPrompt],
  );

  useImperativeHandle(ref, () => ({
    clear: () => {
      floatingImageRef.current = null;
      setFloatingImage(null);
      placementDraggingRef.current = false;
      fillBackground();
      snapshot();
    },
    getCanvas: () => canvasRef.current,
    undo: () => {
      const snap = history.undo();
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");
      if (snap && ctx) ctx.putImageData(snap, 0, 0);
    },
    redo: () => {
      const snap = history.redo();
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");
      if (snap && ctx) ctx.putImageData(snap, 0, 0);
    },
    canUndo: history.canUndo,
    canRedo: history.canRedo,
    stampImage: async (path: string, scale: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      try {
        const img = await loadImage(path);
        const fitScale = Math.min(
          (canvas.width * scale) / img.width,
          (canvas.height * scale) / img.height,
        );
        const w = img.width * fitScale;
        const h = img.height * fitScale;
        const next: FloatingImage = {
          img,
          bitmapW: w,
          bitmapH: h,
          bitmapX: (canvas.width - w) / 2,
          bitmapY: (canvas.height - h) / 2,
        };
        floatingImageRef.current = next;
        placementDraggingRef.current = false;
        setFloatingImage(next);
      } catch (err) {
        console.error("stampImage failed", err);
      }
    },
  }));

  const floatingStyle = (() => {
    if (!floatingImage) return null;
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    if (canvas.width === 0 || canvas.height === 0) return null;
    const cssPerBitmapX = rect.width / canvas.width;
    const cssPerBitmapY = rect.height / canvas.height;
    return {
      left: floatingImage.bitmapX * cssPerBitmapX,
      top: floatingImage.bitmapY * cssPerBitmapY,
      width: floatingImage.bitmapW * cssPerBitmapX,
      height: floatingImage.bitmapH * cssPerBitmapY,
    };
  })();

  return (
    <div className="canvas-stage">
      <canvas
        ref={canvasRef}
        className="canvas-surface"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      />
      {floatingImage && floatingStyle && (
        <img
          src={floatingImage.img.src}
          className="canvas-floating-image"
          style={floatingStyle}
          draggable={false}
          alt=""
        />
      )}
      {textPrompt && (
        <TextOverlay
          prompt={textPrompt}
          canvasRef={canvasRef}
          onCommit={handleTextCommit}
          onCancel={() => {
            if (textPrompt) textPrompt.commit("");
            setTextPrompt(null);
          }}
        />
      )}
    </div>
  );
});
